/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import Image from 'next/image'
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { AnalyticsContext } from '../../context/analyticsContext'
import { FullScreenVideoContext } from '../../context/fullScreenVideoContext'
import { VideoContext } from '../../context/viddyVideoContext'
import { CollectionPlayerVideo, PlaylistPlayerVideo } from '../../types'
import { ControlsContainer } from '../styled/playerComponents'
import { VideoContainer } from '../styled/storiesComponents'
import { LoadingScreen } from './LoadingScreen'
import { VideoProgressTrackerAndScrubber } from './VideoScrubber'

type VideoPlayerProps = {
    onEnded?: () => void
    playerVideo: CollectionPlayerVideo | PlaylistPlayerVideo
    setVideoElementRef?: (videoRef: React.RefObject<HTMLVideoElement>) => void
    isActive: boolean
}

type VideoLoadState = 'UNLOADED' | 'LOADING' | 'LOADED' | 'DIED' | 'NOT_ALLOWED'
type VideoPlayState = 'NOT_STARTED' | 'PLAYING' | 'PAUSED' | 'ENDED'

const getObjectFit = (aspectRatio: string): 'contain' | 'cover' => {
    const [vaW, vaH] = aspectRatio.split(':').map((i) => parseInt(i, 10))
    const videoAspectRatio = vaW / vaH
    const verticalAspectRatio = 9 / 16
    return Math.abs(videoAspectRatio - verticalAspectRatio) < 0.05
        ? 'cover'
        : 'contain'
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
    playerVideo,
    isActive,
    setVideoElementRef,
    onEnded,
}) => {
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const {
        isMuted,
        setIsMuted,
        isBlurred,
        firstVideoReady,
        setFirstVideoReady,
        setIsLPM,
        setIsBlurred,
        videoTapped,
        setVideoTapped,
        shouldPlay,
    } = useContext(VideoContext)
    const { Analytics } = useContext(AnalyticsContext)
    const { isFullScreenPlayerOpen } = useContext(FullScreenVideoContext)
    const [videoLoadState, setVideoLoaded] =
        useState<VideoLoadState>('UNLOADED')
    const [videoPlayState, setVideoPlayState] =
        useState<VideoPlayState>('NOT_STARTED')
    const [currentVideoShouldPlay, setCurrentVideoShouldPlay] = useState(false)
    const [objectFit, setObjectFit] = useState<'contain' | 'cover'>('cover')

    // Provide videoElement ref to HLSManager once the element is attached
    const attachVideoElement = useCallback(
        (element: HTMLVideoElement | null) => {
            videoRef.current = element
            if (element) setVideoElementRef?.(videoRef)
        },
        [setVideoElementRef],
    )

    useEffect(() => {
        setTimeout(() => {
            const videoElement = videoRef.current
            if (!videoElement) return
            if (videoElement.readyState === 0) {
                setIsLPM(true)
            }
        }, 3000)
    }, [])

    // Set whether or not video should be playing (current video & isOpen).
    // A load can finish before activation; onCanPlay will not fire again, so
    // sample the element's readyState while adjusting to the new props.
    const [prevActivation, setPrevActivation] = useState({
        isActive: false,
        shouldPlay: false,
    })
    if (
        prevActivation.isActive !== isActive ||
        prevActivation.shouldPlay !== shouldPlay
    ) {
        setPrevActivation({ isActive, shouldPlay })
        if (isActive && shouldPlay) {
            setCurrentVideoShouldPlay(true)
            if (videoRef.current?.readyState === 4) {
                setVideoLoaded('LOADED')
            }
        }
    }

    useEffect(() => {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (!isActive || !shouldPlay) return
        const videoElement = videoRef.current
        if (!videoElement) return
        videoElement.currentTime = 0
    }, [isActive, shouldPlay])

    // Set object fit for player once the video has loaded (adjusted during render)
    const [prevLoadState, setPrevLoadState] = useState(videoLoadState)
    if (prevLoadState !== videoLoadState) {
        setPrevLoadState(videoLoadState)
        if (videoLoadState === 'LOADED') {
            setObjectFit(getObjectFit(playerVideo.aspectRatio))
        }
    }

    // Toggle playing
    useEffect(() => {
        const videoElement = videoRef.current
        if (!videoElement) return

        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (!isActive || !shouldPlay || !currentVideoShouldPlay) {
            if (!videoElement.paused) {
                videoElement.pause()
            }
            return
        }

        // Video is active and should play

        if (isBlurred) {
            if (videoLoadState === 'LOADED' && !firstVideoReady) {
                playVideo(videoElement).catch((_e: Error) => {
                    if (!firstVideoReady) {
                        setFirstVideoReady(true)
                    }
                })
            } else {
                if (!videoElement.paused) videoElement.pause()
            }
            return
        }

        // Video is active, should play, and not blurred
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (videoLoadState === 'LOADED') {
            playVideo(videoElement).catch((e: Error) => {
                if (!firstVideoReady) {
                    setFirstVideoReady(true)
                }
                if (e.name.includes('NotAllowedError')) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    !videoTapped && setIsLPM(true)
                }
                setVideoLoaded('NOT_ALLOWED')
            })
        }
        return
    }, [
        isActive,
        videoLoadState,
        videoPlayState,
        isBlurred,
        currentVideoShouldPlay,
        shouldPlay,
    ])

    const playVideo = useCallback(
        (videoElement: HTMLVideoElement) => {
            return videoElement.play().then(() => {
                if (!firstVideoReady) {
                    setFirstVideoReady(true)
                }
            })
        },
        [firstVideoReady],
    )

    // Pause on the last frame
    const _onEnded: React.ReactEventHandler<HTMLVideoElement> = () => {
        setVideoPlayState('ENDED')
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        onEnded && onEnded()
    }

    const _onVideoClick = useCallback(() => {
        setVideoTapped(true)
        if (!isActive) return

        setIsBlurred(false)

        const videoElement = videoRef.current
        if (!videoElement) return

        if (!videoTapped) {
            setIsMuted(false)
            videoElement.muted = false
            Analytics.TRACK_VIDEO_PLAYER_MUTE_TOGGLE('unmuted', 'video_tap')
            return
        }

        if (shouldPlay && !currentVideoShouldPlay) {
            setCurrentVideoShouldPlay(true)
            playVideo(videoElement)
            Analytics.TRACK_VIDEO_PLAYER_PLAY_TOGGLE('play')
        } else {
            setCurrentVideoShouldPlay(false)
            videoElement.pause()
            Analytics.TRACK_VIDEO_PLAYER_PLAY_TOGGLE('pause')
        }
    }, [isActive, currentVideoShouldPlay, isBlurred, shouldPlay, videoTapped])

    useEffect(() => {
        if (isFullScreenPlayerOpen) {
            setCurrentVideoShouldPlay(false)
        }
    }, [isFullScreenPlayerOpen])

    // Toggle muting
    useEffect(() => {
        const videoElement = videoRef.current
        if (!videoElement) return
        videoElement.muted = isMuted
    }, [isMuted])

    const videoPlayerStyles: React.CSSProperties = useMemo(() => {
        return { ...videoPlayerStyleObject, objectFit } as React.CSSProperties
    }, [objectFit])

    const _onPlay = useCallback(() => {
        setVideoPlayState('PLAYING')
        setVideoLoaded('LOADED')
    }, [])

    const onError = useCallback(
        (_e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
            const videoElement = videoRef.current
            if (!videoElement) return

            if (
                videoElement.src.includes('mp4') &&
                videoElement.src.includes('medium')
            ) {
                videoElement.pause()
                videoElement.src = videoElement.src.replace('medium', 'low')
                videoElement.load()
            }
        },
        [],
    )

    return (
        <VideoContainer>
            {playerVideo && (
                <VideoProgressTrackerAndScrubber
                    playerVideo={playerVideo}
                    videoRef={videoRef}
                />
            )}
            <ControlsContainer>
                {playerVideo.poster && (
                    <Image
                        style={{
                            objectFit: 'cover',
                            width: '100%',
                            height: '100%',
                            filter: 'blur(50px)',
                            position: 'absolute',
                            zIndex: -2,
                        }}
                        height={0}
                        width={0}
                        sizes="100vw"
                        src={playerVideo.poster}
                        alt=""
                    />
                )}
                <LoadingScreen />
                <VideoPlayerContainer onClick={_onVideoClick}>
                    <video
                        onError={onError}
                        playsInline={true}
                        preload={'metadata'}
                        muted={isMuted}
                        controls={false}
                        poster={playerVideo?.poster}
                        onWaiting={() => setVideoLoaded('LOADING')}
                        onCanPlay={() => setVideoLoaded('LOADED')}
                        onPlay={_onPlay}
                        onPause={() => setVideoPlayState('PAUSED')}
                        onEnded={_onEnded}
                        autoPlay={false}
                        style={videoPlayerStyles}
                        ref={attachVideoElement}
                    ></video>
                </VideoPlayerContainer>
            </ControlsContainer>
        </VideoContainer>
    )
}

const videoPlayerStyleObject: React.CSSProperties = {
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: 'inherit',
    position: 'relative',
    pointerEvents: 'none',
    zIndex: -1,
}

const VideoPlayerContainer = styled.div({
    width: '100%',
    height: '100%',
    borderRadius: 'inherit',
    pointerEvents: 'auto',
    position: 'relative',
})
